import {
  Button,
  Checkbox,
  Col,
  Form,
  FormProps,
  Input,
  Layout,
  message,
  Row,
} from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LOCAL_STORAGE_KEYS } from "../common/constants";
import { usePortalLoginMutation } from "../graphql/generated/hooks";

function Login() {
  const [login, { loading }] = usePortalLoginMutation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    const sessionToken = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
    if (sessionToken && refreshToken) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type FieldType = {
    email?: string;
    password?: string;
    remember?: boolean;
  };

  const onFinish: FormProps<FieldType>["onFinish"] = (values) => {
    if (!values.email || !values.password) {
      return;
    }
    login({
      variables: {
        email: values.email,
        password: values.password,
      },
    })
      .then((res) => {
        if (res.data?.portalLogin) {
          const authResponse = res.data.portalLogin;

          // Store both access token and refresh token
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.SESSION_TOKEN,
            authResponse.accessToken
          );
          localStorage.setItem(
            LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
            authResponse.refreshToken
          );

          // Handle remember me
          if (values.remember) {
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.LOGIN_EMAIL,
              values.email || ""
            );
          } else {
            localStorage.removeItem(LOCAL_STORAGE_KEYS.LOGIN_EMAIL);
          }

          navigate("/", { replace: true });
        } else {
          messageApi.error(t("message.error.login_failed"));
        }
      })
      .catch((e) => {
        messageApi.error(t("message.error.login_failed"));
      });
  };

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = (
    errorInfo
  ) => {
    // Form validation failed
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Row justify="center" align="middle" style={{ minHeight: "100vh", padding: "20px" }}>
        <Col xs={24} sm={20} md={16} lg={12} xl={8} xxl={6}>
          {contextHolder}
          <Form
            dir={i18n.dir()}
            name="login"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item<FieldType>
              name="email"
              initialValue={localStorage.getItem(
                LOCAL_STORAGE_KEYS.LOGIN_EMAIL
              )}
              rules={[
                {
                  required: true,
                  message: t("message.error.email_required"),
                  type: "email",
                },
              ]}
            >
              <Input placeholder={t("placeholder.email")} />
            </Form.Item>

            <Form.Item<FieldType>
              name="password"
              rules={[
                {
                  required: true,
                  message: t("message.error.password_required"),
                },
              ]}
            >
              <Input.Password placeholder={t("placeholder.password")} />
            </Form.Item>
            <Row justify="space-between" gutter={[8, 8]}>
              <Col xs={24} sm={12}>
                <Form.Item<FieldType> name="remember" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>{t("checkbox.remember_me")}</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} style={{ textAlign: i18n.dir() === "rtl" ? "left" : "right" }}>
                <Form.Item<FieldType> style={{ marginBottom: 0 }}>
                  <a
                    onClick={() => {
                      navigate("/forgot-password");
                    }}
                  >
                    {t("button.forgot_password")}
                  </a>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button loading={loading} type="primary" htmlType="submit" block>
                {t("button.login")}
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Layout>
  );
}

export default Login;
